import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { 
  setModuleFilter, 
  resetModuleFilter, 
  selectModuleFilter 
} from "../store/slices/filterSorting.slice";

/**
 * Custom hook for managing filter state with Redux persistence
 * 
 * @param {string} filterModule - Module name for Redux persistence
 * @param {Function} applyCallback - Callback function when filters are applied
 * @param {Function} clearCallback - Callback function when filters are cleared
 * @param {Object} initialFilters - Initial filter values
 * @returns {Object} Filter state and methods
 * 
 * @example
 * const { filters, setFilter, applyFilters, clearFilters, hasActiveFilters } = useFilter(
 *   "designation",
 *   (filters) => fetchData(filters),
 *   () => fetchData({})
 * );
 */
export const useFilter = (
  filterModule = "default",
  applyCallback = () => {},
  clearCallback = () => {},
  initialFilters = {}
) => {
  const dispatch = useDispatch();
  
  // Get persisted filter state from Redux
  const persistedFilters = useSelector((state) => 
    selectModuleFilter(state, filterModule)
  );
  
  // Local filter state
  const [filters, setFiltersState] = useState(
    persistedFilters?.filters || initialFilters
  );

  // Sync with persisted state when component mounts or persisted state changes
  useEffect(() => {
    if (persistedFilters?.filters) {
      setFiltersState(persistedFilters.filters);
    }
  }, [persistedFilters]);

  /**
   * Set a single filter value
   * @param {string} key - Filter key
   * @param {any} value - Filter value
   */
  const setFilter = useCallback((key, value) => {
    setFiltersState((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  /**
   * Set multiple filter values at once
   * @param {Object} newFilters - Object with filter key-value pairs
   */
  const setFilters = useCallback((newFilters) => {
    setFiltersState((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  /**
   * Apply filters - persists to Redux and calls the apply callback
   */
  const applyFilters = useCallback(() => {
    // Remove empty/null/undefined values
    const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    // Persist to Redux
    dispatch(setModuleFilter({
      module: filterModule,
      filterState: {
        filters: cleanedFilters,
        isFilter: Object.keys(cleanedFilters).length > 0
      }
    }));

    // Call the apply callback with cleaned filters
    applyCallback(cleanedFilters);
  }, [filters, filterModule, dispatch, applyCallback]);

  /**
   * Clear all filters - resets Redux state and calls clear callback
   */
  const clearFilters = useCallback(() => {
    setFiltersState({});
    
    // Clear persisted filters from Redux
    dispatch(resetModuleFilter(filterModule));
    
    // Call the clear callback
    clearCallback();
  }, [filterModule, dispatch, clearCallback]);

  /**
   * Reset filters to initial state without applying
   */
  const resetFilters = useCallback(() => {
    setFiltersState(initialFilters);
  }, [initialFilters]);

  /**
   * Check if there are any active filters
   */
  const hasActiveFilters = persistedFilters?.isFilter || false;

  /**
   * Get the count of active filters
   */
  const activeFilterCount = Object.keys(filters).filter(
    key => filters[key] !== "" && filters[key] !== null && filters[key] !== undefined
  ).length;

  return {
    filters,
    setFilter,
    setFilters,
    applyFilters,
    clearFilters,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
  };
};

export default useFilter;

